import { useMemo, useState } from "react";
import type { AuditLogDto } from "@codestack/shared";
import { Badge, Button, Input, Select } from "@codestack/ui";
import { useApi } from "../lib/useApi";
import { EmptyState, ErrorState, LoadingState } from "../components/States";
import { dateTime, titleCase } from "../lib/format";

type Paginated<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

type SecurityEvent = {
  id: string;
  actorType: string;
  actorLabel: string | null;
  action: string;
  ip: string | null;
  createdAt: string;
  metadata: Record<string, unknown> | null;
};

const actorTone: Record<string, "brand" | "info" | "neutral"> = {
  USER: "brand",
  PLATFORM: "info",
  SYSTEM: "neutral",
};

export function AuditPage() {
  const [view, setView] = useState<"all" | "security">("all");
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [actionInput, setActionInput] = useState("");
  const [actorType, setActorType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      page,
      pageSize: 25,
      action: action || undefined,
      actorType: actorType || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [page, action, actorType, from, to],
  );

  const logs = useApi<Paginated<AuditLogDto>>(
    view === "all" ? "/admin/audit" : null,
    query,
  );
  const security = useApi<SecurityEvent[]>(
    view === "security" ? "/admin/audit/security" : null,
  );

  const applySearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setAction(actionInput.trim());
  };

  const reset = () => {
    setAction("");
    setActionInput("");
    setActorType("");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const hasFilters = Boolean(action || actorType || from || to);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-white">Audit log</h1>
          <p className="text-sm text-ink-400">
            Append-only record of every privileged action and security event.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-white/10 p-1">
          <button
            type="button"
            onClick={() => setView("all")}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              view === "all" ? "bg-white/10 text-white" : "text-ink-400 hover:text-white"
            }`}
          >
            All activity
          </button>
          <button
            type="button"
            onClick={() => setView("security")}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              view === "security" ? "bg-white/10 text-white" : "text-ink-400 hover:text-white"
            }`}
          >
            Security
          </button>
        </div>
      </div>

      {view === "all" ? (
        <>
          <div className="admin-card p-4">
            <form onSubmit={applySearch} className="grid gap-3 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <Input
                  placeholder="Filter by action, e.g. platform.updated"
                  value={actionInput}
                  onChange={(event) => setActionInput(event.target.value)}
                />
              </div>
              <div className="lg:col-span-3">
                <Select
                  value={actorType}
                  onChange={(event) => {
                    setActorType(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All actors</option>
                  <option value="USER">Admin users</option>
                  <option value="PLATFORM">Platforms</option>
                  <option value="SYSTEM">System</option>
                </Select>
              </div>
              <div className="flex gap-2 lg:col-span-2">
                <Button type="submit" className="flex-1">
                  Filter
                </Button>
                {hasFilters ? (
                  <Button type="button" variant="ghost" onClick={reset}>
                    Clear
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:col-span-3">
                <Input
                  label="From"
                  type="date"
                  value={from}
                  onChange={(event) => {
                    setFrom(event.target.value);
                    setPage(1);
                  }}
                />
                <Input
                  label="To"
                  type="date"
                  value={to}
                  onChange={(event) => {
                    setTo(event.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </form>
          </div>

          {logs.error ? <ErrorState message={logs.error} onRetry={logs.reload} /> : null}
          {logs.loading && !logs.data ? <LoadingState label="Loading audit log…" /> : null}

          {logs.data && logs.data.items.length === 0 ? (
            <EmptyState
              title="No audit entries match these filters"
              description="Audit entries are written automatically for every privileged action."
            />
          ) : null}

          {logs.data && logs.data.items.length > 0 ? (
            <>
              <div className="table-shell">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="table-head">
                      <tr>
                        <th className="px-4 py-3 font-semibold">When</th>
                        <th className="px-4 py-3 font-semibold">Actor</th>
                        <th className="px-4 py-3 font-semibold">Action</th>
                        <th className="px-4 py-3 font-semibold">Target</th>
                        <th className="px-4 py-3 font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06]">
                      {logs.data.items.map((entry) => (
                        <tr
                          key={entry.id}
                          onClick={() =>
                            setExpanded((current) => (current === entry.id ? null : entry.id))
                          }
                          className="cursor-pointer align-top transition-colors hover:bg-white/[0.02]"
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-[11px] text-ink-500">
                            {dateTime(entry.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Badge tone={actorTone[entry.actorType] ?? "neutral"}>
                                {titleCase(entry.actorType)}
                              </Badge>
                              <span className="text-[11px] text-ink-400">
                                {entry.actorLabel ?? entry.actorId ?? "—"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-[11px] text-ink-200">
                              {entry.action}
                            </span>
                            {expanded === entry.id && entry.metadata ? (
                              <pre className="mt-2 max-w-md overflow-x-auto rounded-md bg-ink-950 p-2 text-[10px] leading-relaxed text-ink-400">
                                {JSON.stringify(entry.metadata, null, 2)}
                              </pre>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-[11px] text-ink-500">
                            {entry.targetType ? (
                              <>
                                {entry.targetType}
                                <span className="block font-mono text-[10px] text-ink-600">
                                  {entry.targetId?.slice(0, 12) ?? "—"}
                                </span>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px] text-ink-500">
                            {entry.ip ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
                <p className="text-xs text-ink-500">
                  Showing {(logs.data.page - 1) * logs.data.pageSize + 1}–
                  {Math.min(logs.data.page * logs.data.pageSize, logs.data.total)} of{" "}
                  {logs.data.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={logs.data.page <= 1 || logs.loading}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    Previous
                  </Button>
                  <span className="px-2 text-xs text-ink-400">
                    Page {logs.data.page} of {logs.data.totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={logs.data.page >= logs.data.totalPages || logs.loading}
                    onClick={() => setPage((value) => value + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <>
          {security.error ? <ErrorState message={security.error} onRetry={security.reload} /> : null}
          {security.loading && !security.data ? (
            <LoadingState label="Loading security events…" />
          ) : null}

          {security.data && security.data.length === 0 ? (
            <EmptyState
              title="No security events recorded"
              description="Sign-ins, credential changes and rejected signatures will appear here."
            />
          ) : null}

          {security.data && security.data.length > 0 ? (
            <ul className="space-y-2">
              {security.data.map((event) => (
                <li key={event.id} className="admin-card flex items-start gap-3 p-4">
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[11px] text-ink-200">{event.action}</span>
                      <Badge tone={actorTone[event.actorType] ?? "neutral"}>
                        {titleCase(event.actorType)}
                      </Badge>
                    </div>
                    <p className="mt-1 text-[11px] text-ink-500">
                      {event.actorLabel ?? "Unknown actor"} · {event.ip ?? "no ip"} ·{" "}
                      {dateTime(event.createdAt)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}
